import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import classnames from "classnames"
import DataLoader from "../DataLoader"

// How far the bar fills while the files download. The rest stands for
// processing the data and drawing the map, which can't report progress: once
// the downloads finish, the bar eases slowly towards the end on its own (a
// CSS transition, which keeps running while that work blocks the page).
const downloadShare = 0.9
const processingTarget = 0.98

function barScale(progress, ready) {
  if (ready) return 1
  return progress === 1 ? processingTarget : progress * downloadShare
}

// DataLoader's download progress (0 to 1, or null if it can't be known),
// updated at most once per frame while `active`.
function useDownloadProgress(active) {
  const [progress, setProgress] = useState(DataLoader.progress)
  useEffect(() => {
    if (!active) return undefined
    let frame = null
    const stop = DataLoader.onProgress(() => {
      if (frame !== null) return
      frame = requestAnimationFrame(() => {
        frame = null
        setProgress(DataLoader.progress)
      })
    })
    setProgress(DataLoader.progress)
    return () => {
      stop()
      if (frame !== null) cancelAnimationFrame(frame)
    }
  }, [active])
  return progress
}

// The home page's call to action. Reads "Loading data", with a progress bar
// when the download size is known, until the data is ready. Then it fades to
// "Explore the data" and links to the rankings.
export default function ExploreButton({ ready }) {
  const progress = useDownloadProgress(!ready)
  return (
    <>
      <Link
        to="/global"
        className={classnames("exploreButton", { ready })}
        aria-disabled={ready ? undefined : true}
        tabIndex={ready ? undefined : -1}
        onClick={(event) => {
          if (!ready) event.preventDefault()
        }}
      >
        <span className="label loadingLabel" aria-hidden={ready}>
          Loading data
        </span>
        <span className="label exploreLabel" aria-hidden={!ready}>
          Explore the data
        </span>
        {progress === null ? null : (
          <span
            className={classnames("progressBar", {
              processing: progress === 1,
            })}
            style={{ transform: `scaleX(${barScale(progress, ready)})` }}
          />
        )}
      </Link>
      <span className="visuallyHidden" role="status">
        {ready ? "The data has loaded." : ""}
      </span>
    </>
  )
}
